import {
  cancelTypedQueries,
  getTypedQueriesData,
  invalidateTypedQueries,
  setTypedQueriesData,
  useApiClient,
} from '@/hooks/useApiClient'
import type { components } from '@/types/api/v1'
import { NotificationMessage } from '@gravitate-js/excalibrr'
import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import type { Dayjs } from 'dayjs'

import type {
  AssignOrderPayload,
  EquipmentChangePayload,
  GetLinkedSchedulePayload,
  GetSchedulePayload,
  LinkOrderPayload,
  LockingPayload,
  MoveOrderPayload,
  RemoveOrderPayload,
  UnlinkOrderPayload,
} from './requests'

import type { GetDriverScheduleResponse, GetLinkedScheduleResponse, GetQuickStatusResponse } from './responses'
import { optimisticallyAddOrder, optimisticallyAddSchedule } from './util'

export const useDriverSchedules = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()
  const apiClient = useApiClient()

  const useSchedules = ({ start, end }: { start: Dayjs; end: Dayjs }) =>
    apiClient.useQuery('post', '/driver/schedule/overview', {
      body: {
        start_date: start.toISOString(),
        end_date: end.toISOString(),
      },
    })

  const useDeleteSchedule = () =>
    apiClient.useMutation('post', '/driver/delete_event', {
      onMutate: async ({ body }) => {
        await cancelTypedQueries(queryClient, 'post', '/driver/schedule/overview')
        const previous = getTypedQueriesData(queryClient, 'post', '/driver/schedule/overview')
        setTypedQueriesData(queryClient, 'post', '/driver/schedule/overview', {}, (data) =>
          data?.filter((x) => x.event_id !== body.id)
        )
        return { previous }
      },
      onError: (err, _vars, ctx) => {
        if (err?.detail) {
          NotificationMessage(null, err?.detail, true)
        }
        const context = ctx as { previous?: Array<[readonly unknown[], components['schemas']['DriverScheduleDay'][]]> }
        if (!context.previous) return
        context.previous.forEach(([key, value]) => {
          queryClient.setQueryData(key, value)
        })
      },
      onSettled: () => invalidateTypedQueries(queryClient, 'post', '/driver/schedule/overview'),
    })

  const useDriverScheduleQuery = (schedulePayload?: GetSchedulePayload) =>
    useQuery({
      queryKey: ['getDriverScheduleQuery', schedulePayload],
      queryFn: ({ queryKey }): GetDriverScheduleResponse[] =>
        api.post('driver_schedule/overview', {
          ...(queryKey as any[])[1],
          date: (queryKey[1] as GetSchedulePayload)?.viewDate,
        }),
      enabled: !!schedulePayload,
    })

  const useQuickStatusQuery = (quickPayload?: GetSchedulePayload) =>
    useQuery({
      queryKey: ['getQuickStatusQuery', quickPayload],
      queryFn: ({ queryKey }): GetQuickStatusResponse =>
        api.post('driver_schedule/quick_status', (queryKey as any[])[1]),

      enabled: !!quickPayload,
      refetchInterval: 30_000,
    })

  const getLinkedScheduleQuery = (payload: GetLinkedSchedulePayload) =>
    useQuery({
      queryKey: ['getLinkedScheduleQuery', payload],
      queryFn: ({ queryKey }): GetLinkedScheduleResponse =>
        api.post('driver_schedule/other_schedule', (queryKey as any[])[1]),
    })

  const linkOrderMutation = useMutation({
    mutationKey: ['linkOrderMutation'],
    mutationFn: (request: LinkOrderPayload) => api.post('driver_schedule/link', request),
  })

  const unlinkOrderMutation = useMutation({
    mutationKey: ['unlinkOrderMutation'],
    mutationFn: (payload: UnlinkOrderPayload) => api.post('driver_schedule/unlink', payload),
  })

  const assignOrderMutation = useMutation({
    mutationKey: ['assignOrderMutation'],
    mutationFn: (payload: AssignOrderPayload) => api.post('driver_schedule/assign', payload),
    onSuccess: async (response: any, request) => {
      optimisticallyAddSchedule(request.driver_schedule_id, response.driver_schedules[0], queryClient)
    },
  })

  const removeOrderMutation = useMutation({
    mutationKey: ['removeOrderMutation'],
    mutationFn: (payload: RemoveOrderPayload) => api.post('driver_schedule/unassign', payload),
    onSuccess: async (response: any, request) => {
      for (const order of response.add_orders) {
        optimisticallyAddOrder(order, queryClient)
      }

      queryClient.setQueriesData({ queryKey: ['getDriverScheduleQuery'] }, (oldData: GetDriverScheduleResponse) => {
        return oldData?.map((schedule) => {
          if (request?.driver_schedule_id === schedule.id) {
            return response.driver_schedules[0]
          }
          return schedule
        })
      })
    },
  })

  const moveOrderMutation = useMutation({
    mutationKey: ['moveOrderMutation'],
    mutationFn: (payload: MoveOrderPayload) => api.post('driver_schedule/move', payload),

    onSuccess: async (response, request) => {
      await queryClient.invalidateQueries({
        queryKey: ['getAcceptedOrdersQuery'],
      })
      queryClient.setQueriesData({ queryKey: ['getDriverScheduleQuery'] }, (oldData: GetDriverScheduleResponse) => {
        return oldData?.map((schedule) => {
          if (request?.src_driver_schedule_id === schedule.id) {
            return response.driver_schedules?.filter((s) => s.id === request.src_driver_schedule_id)?.[0]
          }
          if (request?.tgt_driver_schedule_id === schedule.id) {
            return response.driver_schedules?.filter((s) => s.id === request.tgt_driver_schedule_id)?.[0]
          }
          return schedule
        })
      })
    },
  })

  const lockTripMutation = useMutation({
    mutationKey: ['lockTripMutation'],
    mutationFn: ({ driver_schedule_id, slot }: LockingPayload) =>
      api.post(`driver_schedule/lock_trip`, undefined, {
        queryParams: new URLSearchParams({ driver_schedule_id, slot: slot.toString() }),
      }),

    onSuccess: async (response, request) => {
      await queryClient.cancelQueries({
        queryKey: ['getDriverScheduleQuery'],
      })
      queryClient.setQueriesData({ queryKey: ['getDriverScheduleQuery'] }, (oldData: GetDriverScheduleResponse) => {
        return oldData?.map((schedule) => {
          if (request?.driver_schedule_id === schedule.id) {
            return response.driver_schedules[0]
          }
          return schedule
        })
      })
    },
  })

  const unlockTripMutation = useMutation({
    mutationKey: ['unlockTripMutation'],
    mutationFn: ({ driver_schedule_id, slot }: LockingPayload) =>
      api.post(`driver_schedule/unlock_trip`, undefined, {
        queryParams: new URLSearchParams({ driver_schedule_id, slot: slot.toString() }),
      }),
    onSuccess: async (response, request) => {
      await queryClient.cancelQueries({
        queryKey: ['getDriverScheduleQuery'],
      })
      queryClient.setQueriesData({ queryKey: ['getDriverScheduleQuery'] }, (oldData: GetDriverScheduleResponse) => {
        return oldData?.map((schedule) => {
          if (request?.driver_schedule_id === schedule.id) {
            return response.driver_schedules?.[0]
          }
          return schedule
        })
      })
    },
  })

  const changeEquipmentMutation = useMutation({
    mutationKey: ['changeEquipmentMutation'],
    mutationFn: (payload: EquipmentChangePayload) => api.post(`driver_schedule/change_equipment`, payload),
  })

  const useAddResourceMutation = useMutation<{}, unknown, { depot: string; start_date: string; end_date: string }>({
    mutationKey: ['addResourceMutation'],
    mutationFn: (resource) =>
      api.post('driver/carrier_depot/add_resource', null, { queryParams: new URLSearchParams(resource) }),
  })

  return {
    useDriverScheduleQuery,
    useSchedules,
    useDeleteSchedule,
    linkOrderMutation,
    unlinkOrderMutation,
    moveOrderMutation,
    lockTripMutation,
    unlockTripMutation,
    useQuickStatusQuery,
    getLinkedScheduleQuery,
    useAddResourceMutation,
    changeEquipmentMutation,
    assignOrderMutation,
    removeOrderMutation,
  }
}
